import { supabase } from '../lib/supabase'

export interface JobTimeEntrySummary {
  id: string
  employee_id: string
  clock_in_time: string
  clock_out_time?: string | null
  total_hours?: number | null
  entry_type?: string | null
  employee?: { first_name?: string; last_name?: string } | null
}

export interface JobInvoiceLink {
  invoice_id: string
  invoice_number: string
  status: string
  total: number
  balance_due: number
}

export interface JobSiblingSummary {
  id: string
  job_number: string
  title: string
  scheduled_date?: string | null
  status: string
}

export interface JobAssignmentSummary {
  crew_id?: string | null
  crew_name?: string | null
  employee_id?: string | null
  employee_name?: string | null
}

export async function fetchJobTimeEntries(jobId: string): Promise<JobTimeEntrySummary[]> {
  const { data, error } = await supabase
    .from('time_entries')
    .select('id, employee_id, clock_in_time, clock_out_time, total_hours, entry_type, employee:employees(first_name, last_name)')
    .eq('job_id', jobId)
    .order('clock_in_time', { ascending: false })

  if (error) throw error
  return (data || []) as JobTimeEntrySummary[]
}

export async function fetchJobInvoiceLinks(jobId: string): Promise<JobInvoiceLink[]> {
  const { data, error } = await supabase
    .from('invoice_items')
    .select('invoice_id, invoice:invoices(id, invoice_number, status, total, balance_due)')
    .eq('job_id', jobId)

  if (error) throw error

  const seen = new Set<string>()
  const links: JobInvoiceLink[] = []
  for (const row of data || []) {
    const inv = (row as any).invoice
    if (!inv?.id || seen.has(inv.id)) continue
    seen.add(inv.id)
    links.push({
      invoice_id: inv.id,
      invoice_number: inv.invoice_number,
      status: inv.status,
      total: Number(inv.total || 0),
      balance_due: Number(inv.balance_due || 0),
    })
  }
  return links
}

export async function fetchJobSiblings(job: {
  id: string
  parent_recurring_job_id?: string | null
  contract_id?: string | null
  visit_group_id?: string | null
}): Promise<JobSiblingSummary[]> {
  let query = supabase
    .from('jobs')
    .select('id, job_number, title, scheduled_date, status')
    .neq('id', job.id)
    .order('scheduled_date', { ascending: true })
    .limit(20)

  if (job.parent_recurring_job_id) {
    query = query.or(
      `parent_recurring_job_id.eq.${job.parent_recurring_job_id},id.eq.${job.parent_recurring_job_id}`
    )
  } else if (job.contract_id && job.visit_group_id) {
    query = query
      .eq('contract_id', job.contract_id)
      .eq('visit_group_id', job.visit_group_id)
  } else {
    query = query.eq('parent_recurring_job_id', job.id)
  }

  const { data, error } = await query
  if (error) throw error
  return (data || []) as JobSiblingSummary[]
}

export async function fetchJobAssignment(job: {
  assigned_crew_id?: string | null
  assigned_employee_id?: string | null
}): Promise<JobAssignmentSummary> {
  const summary: JobAssignmentSummary = {}
  if (job.assigned_crew_id) {
    const { data } = await supabase
      .from('crews')
      .select('id, name')
      .eq('id', job.assigned_crew_id)
      .maybeSingle()
    summary.crew_id = job.assigned_crew_id
    summary.crew_name = data?.name || null
  }
  if (job.assigned_employee_id) {
    const { data } = await supabase
      .from('employees')
      .select('id, first_name, last_name')
      .eq('id', job.assigned_employee_id)
      .maybeSingle()
    summary.employee_id = job.assigned_employee_id
    summary.employee_name = data
      ? `${data.first_name || ''} ${data.last_name || ''}`.trim()
      : null
  }
  return summary
}
